// ── morph ────────────────────────────────────────────────────────────────────
//
// Patch a live DOM tree toward a new HTML string instead of replacing it.
//
// Why this exists: the app renders by template strings and used to do
// `root.innerHTML = html` on every state change. That destroys and rebuilds
// the whole shell for a single "Felt it" tap — every CSS transition restarts,
// hover states flicker, avatars re-download, focus and scroll have to be
// snapshotted and put back by hand, and entrance animations replay on content
// that never left. Morphing keeps every node that is still the same node and
// only touches what actually changed, so the page behaves like a page.
//
// Rules:
//  • Elements match by position, same tag, and — when either side carries a
//    `data-key` or `id` — the same key. Keyed siblings are re-ordered rather
//    than recreated, so a list that gains one item keeps the other items.
//  • Attributes are diffed; unchanged attributes are not rewritten.
//  • A focused <input>/<textarea>/<select> keeps what the user is typing; the
//    template's value is applied only when the field is not being edited.
//  • Text nodes are updated in place.
//
// It is deliberately small and has no opinions about components; it only
// understands HTML.

const FORM_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

// The value the template gave each form control at the last render. While a
// control is focused, the user's typing wins over a template that still says
// the OLD value — but if the template's value CHANGED (the app cleared a
// composer after sending, or loaded a draft), that change is intentional and
// is applied even mid-focus.
const lastTemplateValue = new WeakMap<Element, string>();

function shouldApplyValue(to: Element, editing: boolean, templateValue: string): boolean {
  const previous = lastTemplateValue.get(to);
  lastTemplateValue.set(to, templateValue);
  if (!editing) return true;
  return previous !== undefined && previous !== templateValue;
}

function keyOf(node: Element): string | null {
  return node.getAttribute("data-key") || (node.id ? `#${node.id}` : null);
}

function sameNode(a: Node, b: Node): boolean {
  if (a.nodeType !== b.nodeType) return false;
  if (a.nodeType !== Node.ELEMENT_NODE) return true;
  const ea = a as Element;
  const eb = b as Element;
  if (ea.tagName !== eb.tagName) return false;
  const ka = keyOf(ea);
  const kb = keyOf(eb);
  if (ka === null && kb === null) return true;
  return ka === kb;
}

function syncAttributes(from: Element, to: Element): void {
  // Remove attributes that disappeared.
  for (let i = to.attributes.length - 1; i >= 0; i -= 1) {
    const name = to.attributes[i].name;
    if (!from.hasAttribute(name)) to.removeAttribute(name);
  }
  // Set attributes that changed.
  for (let i = 0; i < from.attributes.length; i += 1) {
    const { name, value } = from.attributes[i];
    if (to.getAttribute(name) !== value) to.setAttribute(name, value);
  }
}

function syncFormState(from: Element, to: Element): void {
  const editing = document.activeElement === to;
  if (to instanceof HTMLInputElement && from instanceof HTMLInputElement) {
    const type = to.type;
    if (type === "checkbox" || type === "radio") {
      if (to.checked !== from.checked) to.checked = from.checked;
      return;
    }
    if (shouldApplyValue(to, editing, from.value) && to.value !== from.value) to.value = from.value;
    return;
  }
  if (to instanceof HTMLTextAreaElement && from instanceof HTMLTextAreaElement) {
    if (shouldApplyValue(to, editing, from.value) && to.value !== from.value) to.value = from.value;
    return;
  }
  if (to instanceof HTMLSelectElement && from instanceof HTMLSelectElement) {
    if (shouldApplyValue(to, editing, from.value) && to.value !== from.value) to.value = from.value;
  }
}

function morphChildren(from: Element, to: Element): void {
  const fromChildren = Array.from(from.childNodes);
  const toChildren = Array.from(to.childNodes);

  // Index keyed live children so a keyed template child can find its
  // existing node even if siblings were inserted or removed before it.
  const keyedLive = new Map<string, Element>();
  for (const child of toChildren) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const k = keyOf(child as Element);
      if (k !== null) keyedLive.set(`${(child as Element).tagName}|${k}`, child as Element);
    }
  }

  let liveIndex = 0;
  for (let i = 0; i < fromChildren.length; i += 1) {
    const want = fromChildren[i];
    let live: ChildNode | undefined = to.childNodes[liveIndex];

    // Keyed template node: prefer its existing counterpart wherever it is.
    if (want.nodeType === Node.ELEMENT_NODE) {
      const k = keyOf(want as Element);
      if (k !== null) {
        const found = keyedLive.get(`${(want as Element).tagName}|${k}`);
        if (found && found !== live) {
          to.insertBefore(found, live ?? null);
          live = found;
        }
      }
    }

    if (live && sameNode(want, live)) {
      morphNode(want, live);
      liveIndex += 1;
      continue;
    }

    // No match at this position: insert the new node here. If the live node
    // at this position is unkeyed and a different kind, it will fall off the
    // end and be removed below.
    const clone = want.cloneNode(true);
    to.insertBefore(clone, live ?? null);
    liveIndex += 1;
  }

  // Anything left over in the live tree no longer exists in the template.
  while (to.childNodes.length > fromChildren.length) {
    const extra = to.childNodes[to.childNodes.length - 1];
    if (!extra) break;
    to.removeChild(extra);
  }
}

function morphNode(from: Node, to: Node): void {
  if (from.nodeType === Node.TEXT_NODE || from.nodeType === Node.COMMENT_NODE) {
    if (to.nodeValue !== from.nodeValue) to.nodeValue = from.nodeValue;
    return;
  }
  if (from.nodeType !== Node.ELEMENT_NODE) return;
  const fe = from as Element;
  const te = to as Element;
  syncAttributes(fe, te);
  if (FORM_TAGS.has(te.tagName)) {
    syncFormState(fe, te);
    // <select> options still need diffing; inputs/textarea carry no children
    // worth morphing (textarea's text IS its value — handled above).
    if (te.tagName !== "SELECT") return;
  }
  morphChildren(fe, te);
}

const parser = document.createElement("template");

/** Morph `root`'s children to match `html`. */
export function morph(root: Element, html: string): void {
  parser.innerHTML = html;
  morphChildren(parser.content as unknown as Element, root);
}
